import React, { ReactElement, SetStateAction } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useTranslation } from 'react-i18next';
import SkipItemIcon from '../../../../../assets/icons/SkipItemIcon';

import { setActivePickID } from '../../../../../redux/actions/activePickTour.actions';
import { getHasJustScannedFromState, isHubLabelPrintingEnabled } from '../../../../../redux/selectors';
import { Pick, PickLocation } from '../../../../../types';
import {
  ORDER_TYPE_COMMERCIAL_EP,
  ORDER_TYPE_COMMERCIAL,
  OTHER,
  POG_DYNAMIC_SLOT,
  POG_OVERSTOCK,
  PLANO_ID_NOT_ON_PLANOGRAM,
} from '../../../../../constants';

import {
  mergeClassNames,
  displayOrderType,
  determineIconByOrderType,
  generatePickID,
  parseDynamicSlotDetails,
  calculateAmountToPick,
} from '../../../../../utils';
import { ReprintButton } from '../ReprintButton/ReprintButton';

import styles from './PickItem.css';

type Props = {
  pick: Pick;
  isActive?: boolean;
  isTourComplete?: boolean;
  setActiveTab?: (value: SetStateAction<'Details View' | 'List View'>) => void;
};

const PickItem = ({ pick, isActive, isTourComplete, setActiveTab }: Props): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasJustScanned = useSelector(getHasJustScannedFromState);
  const isLabelPrintingEnabled = useSelector(isHubLabelPrintingEnabled);
  const translationRoot = 'ACTIVE_PICK_TOUR.LIST_VIEW';

  const {
    isSkipped,
    orderType,
    pickedQuantity,
    partNumber,
    lineNumber,
    locations,
    skipReason,
    description,
    isEligibleForPicking,
    destination,
    locationIndex,
  } = pick;

  const isTourDoneAndSkipped = isTourComplete && isSkipped;
  const isCommercialEP = orderType === ORDER_TYPE_COMMERCIAL_EP;
  const isCommercial = orderType === ORDER_TYPE_COMMERCIAL;
  const visibleLocations = locations.filter(location => location.isVisible);
  const amountToPick = calculateAmountToPick(pick);
  const enableReprintLabel = isCommercialEP && pickedQuantity === amountToPick;
  const isPartialPickedAndSkipped = isCommercialEP && pickedQuantity > 0 && isSkipped;

  const renderReprintLabel = () =>
    (enableReprintLabel || isPartialPickedAndSkipped) &&
    isLabelPrintingEnabled && <ReprintButton pickLineNumber={lineNumber} />;

  const displaySkipInfo = () => {
    switch (orderType) {
      case 'OVERSTOCK': {
        if (pickedQuantity > 0) {
          return t(`${translationRoot}.ITEM_ADJUSTED`);
        }
        return t(`${translationRoot}.ITEM_NOT_FOUND`);
      }
      case 'SAT2SAT': {
        return `${t(`${translationRoot}.ALL_LOCATIONS_SKIPPED`)} | ${t(
          `${translationRoot}.${skipReason === OTHER ? 'OTHER' : 'QTY_ON_HAND'}`
        )}`;
      }
      default: {
        return t(`${translationRoot}.ALL_LOCATIONS_SKIPPED`);
      }
    }
  };

  const displayLocation = (location: PickLocation) => {
    if (location.planogramName === POG_DYNAMIC_SLOT) {
      const dynamicSlotDetails = parseDynamicSlotDetails(location.itemSequence.toString());
      return `${t(`${translationRoot}.ZONE`)} ${dynamicSlotDetails.zone} | ${t(`${translationRoot}.AISLE`)} ${
        dynamicSlotDetails.aisle
      } | ${t(`${translationRoot}.SLOT`)} ${dynamicSlotDetails.slot}`;
    }
    if (location.planogramName === POG_OVERSTOCK) {
      return `${t(`${translationRoot}.POG_FOG`)}: ${t(`${translationRoot}.OVERSTOCK`)} | ${t(
        `${translationRoot}.SLOT`
      )}: ${location.itemSequence || 0}`;
    }
    if (location.planoID === PLANO_ID_NOT_ON_PLANOGRAM) {
      return `${t(`${translationRoot}.POG_FOG`)}: ${t(`${translationRoot}.NOT_ON_PLANOGRAM`)} | ${t(
        `${translationRoot}.SEQ`
      )}: ${location.itemSequence || 0}`;
    }
    return `${t(`${translationRoot}.POG_FOG`)}: ${location.planogramName} | ${t(
      `${translationRoot}.SEQ`
    )}: ${location.itemSequence || 0}`;
  };

  return (
    <li
      className={mergeClassNames(
        styles.root,
        isActive && visibleLocations.length < 2 && (hasJustScanned ? styles.scanned : styles.active),
        isTourDoneAndSkipped && visibleLocations.length < 2 && styles.isSkipped,
        isCommercial && (pickedQuantity === amountToPick || isSkipped) && styles.commercialComplete
      )}
      onClick={() => {
        if (isTourDoneAndSkipped && visibleLocations.length < 2) {
          dispatch(setActivePickID({ id: generatePickID(pick) }));
          if (setActiveTab) {
            setActiveTab('Details View');
          }
        }
      }}
    >
      <div className={styles.details}>
        <div className={styles.detailsLine}>
          <span className={styles.description}>{description}</span>
          <div className={styles.qtyContainer}>
            {isEligibleForPicking ? (
              <>
                <span className={styles.qtyLabel}>{t(`${translationRoot}.ITEM_QTY`)}</span>
                <span className={styles.qty}>
                  {pickedQuantity}/{amountToPick}
                </span>
              </>
            ) : (
              <span className={styles.nonPickable}>{t(`${translationRoot}.NON_PICKABLE`)}</span>
            )}
          </div>
        </div>
        <div className={styles.detailsLine}>
          <div className={styles.orderTypeContainer}>
            {determineIconByOrderType({ orderType, small: true })}
            <span className={styles.orderType}>{displayOrderType(orderType, null, destination)}</span>
          </div>
          <span className={styles.partNumber}>
            {t(`${translationRoot}.PART_NUMBER`)}: {partNumber}
          </span>
        </div>
      </div>
      <ul>
        {isSkipped && (
          <li className={styles.skipContainer}>
            <span className={styles.skip}>
              <SkipItemIcon small />
              {displaySkipInfo()}
            </span>
            <span>{renderReprintLabel()}</span>
          </li>
        )}
        {visibleLocations.length > 0 ? (
          visibleLocations.map((location, index) => (
            <li
              key={`${location.planogramName}:${location.itemSequence}:${location.vdlSequence}:${location.planoID ||
                0}`}
              className={mergeClassNames(
                styles.locationContainer,
                isActive &&
                  visibleLocations.length > 1 &&
                  locationIndex === index &&
                  (hasJustScanned ? styles.scanned : styles.active),
                isTourDoneAndSkipped && visibleLocations.length > 1 && styles.isSkipped
              )}
              onClick={() => {
                if (isTourDoneAndSkipped && visibleLocations.length > 1) {
                  dispatch(setActivePickID({ id: generatePickID(pick), locationIndex: index }));
                  if (setActiveTab) {
                    setActiveTab('Details View');
                  }
                }
              }}
            >
              <span className={styles.location}>{displayLocation(location)}</span>
              {enableReprintLabel && <span>{renderReprintLabel()}</span>}
            </li>
          ))
        ) : (
          <li className={styles.locationContainer}>
            <span className={styles.location}>{t(`${translationRoot}.NO_LOCATION_DATA`)}</span>
            {enableReprintLabel && <span>{renderReprintLabel()}</span>}
          </li>
        )}
      </ul>
    </li>
  );
};

export default PickItem;
